import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredVertical } from '../lib/anchoredPlacement';
import { useConnectionStore, type ConnStatus } from '../lib/stores';
import { readerLocale } from '../lib/locale';

// Reuse existing status-dot palette (index.css): connected=green, syncing=amber
// pulse, error=red, disconnected=grey.
const DOT_CLASS: Record<ConnStatus, string> = {
  live: 'status-dot-connected',
  connecting: 'status-dot-syncing',
  reconnecting: 'status-dot-syncing',
  offline: 'status-dot-error',
  paused: 'status-dot-disconnected',
};

const LABEL: Record<ConnStatus, string> = {
  live: 'System Online',
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  offline: 'Offline',
  paused: 'Paused',
};

const POPOVER_WIDTH = 260;

/** Relative "Xs / Xm / Xh ago" for the last-event line. */
function relTime(ts: number | null, now: number): string {
  if (ts == null) return '—';
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

/** Track document visibility so the sidebar can show a "Paused" overlay while the tab
 *  is hidden WITHOUT the store ever storing 'paused' — the store keeps the true
 *  connection status, so hidden-tab reconnects are still counted/logged. */
function useDocumentHidden(): boolean {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  return hidden;
}

/** The popover body — a separate component so it only mounts (and only subscribes to
 *  the per-event `lastEventAt`/`reconnects`/`log`) while open. That keeps the always-
 *  mounted sidebar trigger from re-rendering on every realtime event. */
function ConnectionPopover({ status, pos, onClose }: {
  status: ConnStatus;
  pos: { left: number; rect: DOMRect };
  onClose: () => void;
}) {
  const lastEventAt = useConnectionStore((s) => s.lastEventAt);
  const reconnects = useConnectionStore((s) => s.reconnects);
  const log = useConnectionStore((s) => s.log);
  const [now, setNow] = useState(() => Date.now());

  // Tick the relative clock; close on scroll/resize/Escape (the fixed popover would
  // otherwise drift from the trigger).
  useEffect(() => {
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    window.addEventListener('keydown', onKey);
    return () => {
      clearInterval(t);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const recent = useMemo(() => [...log].reverse().slice(0, 6), [log]);
  const rowStyle = { display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' } as const;

  // The trigger lives in the sidebar footer, so this all but always resolves to
  // "upward" — the point of going through the shared helper is the CAP: the old
  // hand-rolled `calc(100vh - 16px)` wasn't the room above, so on a short window the
  // panel's top ran off screen, and top overflow can't be scrolled back (b-01764).
  const { panelRef, place } = useAnchoredVertical(pos.rect);

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000 }} />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Live connection status"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', left: pos.left, zIndex: 2001, width: POPOVER_WIDTH,
          top: place?.up ? undefined : place?.top,
          bottom: place?.up ? place.bottom : undefined,
          maxWidth: 'calc(100vw - 16px)', maxHeight: place?.maxHeight, overflowY: 'auto',
          background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius-md)', boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
          padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 600, fontSize: 'var(--text-sm)' }}>
          <span className={`status-dot ${DOT_CLASS[status]}`} />
          {LABEL[status]}
        </div>
        <div style={rowStyle}><span>Last event</span><span>{relTime(lastEventAt, now)}</span></div>
        <div style={rowStyle}><span>Reconnects</span><span>{reconnects} this session</span></div>
        {recent.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-2)' }}>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)', marginBottom: 'var(--space-1)' }}>Recent</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recent.map((e, i) => (
                <div key={`${e.ts}:${i}`} style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                    {new Date(e.ts).toLocaleTimeString(readerLocale(), { timeStyle: 'medium' })}
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>{e.note}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

/** Sidebar footer indicator for the live SSE connection. Dot colour + label reflect
 *  the connection state; clicking opens a popover with last-event time, reconnect
 *  count, and a recent connection-event mini-log. */
export function ConnectionStatus() {
  const status = useConnectionStore((s) => s.status);
  const hidden = useDocumentHidden();
  // 'paused' is a DISPLAY overlay only (tab hidden) — never stored, so hidden-tab
  // reconnects still register in the store's counter/log. 'offline' shows through.
  const display: ConnStatus = hidden && status !== 'offline' ? 'paused' : status;

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; rect: DOMRect } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  // Stable so ConnectionPopover's listener/interval effect doesn't churn on a
  // parent re-render (status change or visibility flip) while the popover is open.
  const close = useCallback(() => setOpen(false), []);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // The side is no longer hardcoded here — the popover measures itself and picks
      // it, which from the footer means upward, but capped to the room it really has.
      const left = Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 8));
      setPos({ left, rect: r });
    }
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="sidebar-status"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        aria-label={`Live connection: ${LABEL[display]} — details`}
        title={`Live connection: ${LABEL[display]} — click for details`}
        // Reset only the <button> chrome; let .sidebar-status own layout/padding/colour
        // (matches the row's original look and keeps the muted --text-tertiary tone).
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
      >
        <span className={`status-dot ${DOT_CLASS[display]}`} />
        <span className="sidebar-link-text" style={{ fontSize: 'var(--text-xs)' }}>{LABEL[display]}</span>
      </button>

      {open && pos && <ConnectionPopover status={display} pos={pos} onClose={close} />}
    </>
  );
}
